import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuChevronDown, LuX, LuFilter, LuCheck } from 'react-icons/lu';
import { formatosDisponiveis } from './RelatorioResultado';
import {
    carregarValores,
    type BlocoPainel, type Catalogo, type CatalogoFonte, type EspecificacaoRelatorio,
    type ResultadoRelatorio, type ValorDimensao, type VisualRelatorio,
} from '../services/RelatorioPersonalizadoService';

/* O editor de um bloco: a frase em português que substituiu os cinco passos
   numerados do construtor.

   O construtor pedia fonte, depois medida, depois dimensão, depois filtro,
   depois visual — que é a anatomia de uma ferramenta de BI, não a de uma
   pergunta. Ele montou um e o resultado foi "um número que não me diz nada".
   A frase diz a mesma coisa na ordem em que a pessoa pensa:

       Em Negócios, mostrar quantidade
       separado por situação
       contando pela data de criação

   As lacunas são as mesmas escolhas de antes — o que mudou é que elas estão
   dentro de uma frase que já se lê antes de ser preenchida, e o resultado
   atrás muda a cada escolha.                                                 */

/* ─── A lacuna ────────────────────────────────────────────────────
   Não é um campo de formulário: é uma palavra sublinhada dentro do texto. O
   menu sai por portal porque o painel lateral rola (overflow), e um menu
   absoluto dentro dele seria cortado na primeira opção.                     */

interface OpcaoLacuna { id: string; nome: string; nota?: string | null; desabilitada?: boolean }

function Lacuna({ valor, opcoes, onEscolher, vazio, multipla, escolhidos }: {
    valor: string;
    opcoes: OpcaoLacuna[];
    onEscolher: (id: string) => void;
    vazio?: string;
    /** Em lista múltipla o menu fica aberto e marca o que já está escolhido. */
    multipla?: boolean;
    escolhidos?: string[];
}) {
    const [aberto, setAberto] = useState(false);
    const botao = useRef<HTMLButtonElement | null>(null);
    const menu = useRef<HTMLDivElement | null>(null);
    const [caixa, setCaixa] = useState<{ top: number; left: number; width: number } | null>(null);

    useLayoutEffect(() => {
        if (!aberto || !botao.current) return;
        const r = botao.current.getBoundingClientRect();
        setCaixa({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 200) });
    }, [aberto]);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => {
            const alvo = e.target as Node;
            if (botao.current?.contains(alvo) || menu.current?.contains(alvo)) return;
            setAberto(false);
        };
        const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', fora);
            document.removeEventListener('keydown', esc);
        };
    }, [aberto]);

    return (
        <>
            <button type="button" ref={botao} className={`rl-lacuna ${valor ? '' : 'is-vazia'}`}
                aria-expanded={aberto} onClick={() => setAberto(a => !a)}>
                <span>{valor || vazio || 'escolher'}</span>
                <LuChevronDown size={11} />
            </button>

            {aberto && caixa && createPortal(
                <div ref={menu} className="rl-menu" style={{ top: caixa.top, left: caixa.left, minWidth: caixa.width }}>
                    {opcoes.length === 0 && <span className="rl-menu-vazio">Nada para escolher aqui.</span>}
                    {opcoes.map(o => (
                        <button
                            key={o.id}
                            type="button"
                            className={`rl-menu-i ${escolhidos?.includes(o.id) ? 'is-on' : ''}`}
                            disabled={o.desabilitada}
                            title={o.nota ?? undefined}
                            onClick={() => { onEscolher(o.id); if (!multipla) setAberto(false); }}
                        >
                            <span className="rl-menu-n">{o.nome}</span>
                            {escolhidos?.includes(o.id) && <LuCheck size={12} />}
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </>
    );
}

/* ─── O editor ────────────────────────────────────────────────── */

interface Props {
    bloco: BlocoPainel;
    catalogo: Catalogo;
    /** O resultado do bloco, para dizer quais formatos servem — e por que não servem. */
    resultado: ResultadoRelatorio | null;
    onMudar: (bloco: BlocoPainel) => void;
    onFechar: () => void;
    /** O período do painel, em palavras — o editor só o cita, não o muda. */
    periodoEmPalavras: string;
}

export default function BlocoEditor({ bloco, catalogo, resultado, onMudar, onFechar, periodoEmPalavras }: Props) {
    const consulta = bloco.consulta;
    const fonte: CatalogoFonte | undefined = catalogo.fontes.find(f => f.id === consulta.fonte);

    /** Valores possíveis de cada dimensão usada em filtro, carregados sob demanda. */
    const [valores, setValores] = useState<Record<string, ValorDimensao[] | null>>({});
    /* Quem JÁ foi pedido. Sem isto o pedido sai de novo a cada render enquanto a
       resposta não chega — e alguns desses valores vêm de outro endpoint. */
    const pedidos = useRef<Set<string>>(new Set());

    useEffect(() => {
        consulta.filtros.forEach(f => {
            if (pedidos.current.has(f.dimensao)) return;
            pedidos.current.add(f.dimensao);
            void carregarValores(f.dimensao).then(v =>
                setValores(atual => ({ ...atual, [f.dimensao]: v })));
        });
    }, [consulta.filtros]);

    const mudarConsulta = (parcial: Partial<EspecificacaoRelatorio>) =>
        onMudar({ ...bloco, consulta: { ...consulta, ...parcial } });

    /* Trocar de fonte zera medida, separação e filtro: o vocabulário é outro, e
       carregar "faturamento" para Ligações só produziria um erro do servidor. */
    const trocarFonte = (id: string) => {
        const nova = catalogo.fontes.find(f => f.id === id);
        if (!nova) return;
        onMudar({
            ...bloco,
            consulta: {
                ...consulta,
                fonte: id,
                medidas: nova.medidas[0] ? [{ id: nova.medidas[0].id }] : [],
                dimensoes: [],
                filtros: [],
                periodo: { ...consulta.periodo, data: nova.datas[0]?.id ?? consulta.periodo.data },
            },
        });
    };

    const formatos = formatosDisponiveis(resultado);

    /* Gráfico em bloco de uma linha não desenha — cresce para três, que é onde
       eixo e curva cabem. Mexer na altura aqui evita o formato "escolhido mas
       invisível", que foi exatamente a queixa do construtor antigo. */
    const trocarVisual = (v: VisualRelatorio) => {
        const precisaDeEspaco = v === 'linha' || v === 'pizza' || v === 'barras';
        onMudar({
            ...bloco,
            h: precisaDeEspaco && bloco.h < 3 ? 3 : bloco.h,
            consulta: { ...consulta, visual: v },
        });
    };

    const medidasLivres = (fonte?.medidas ?? []).filter(m => !consulta.medidas.some(x => x.id === m.id));
    const dimensoesLivres = (fonte?.dimensoes ?? []).filter(d => !consulta.dimensoes.includes(d.id));
    const nomeMedida = (id: string) => fonte?.medidas.find(m => m.id === id)?.nome ?? id;
    const nomeDimensao = (id: string) => fonte?.dimensoes.find(d => d.id === id)?.nome ?? id;

    return (
        <aside className="rl-lateral" aria-label="Editar bloco">
            <header className="rl-lat-h">
                <span className="rl-lat-t">{bloco.titulo || 'Bloco'}</span>
                <button type="button" className="rl-lat-x" aria-label="Fechar editor" onClick={onFechar}>
                    <LuX size={14} />
                </button>
            </header>

            <div className="rl-lat-b">
                <p className="rl-frase">
                    Em{' '}
                    <Lacuna
                        valor={fonte?.nome ?? ''}
                        opcoes={catalogo.fontes.map(f => ({ id: f.id, nome: f.nome }))}
                        onEscolher={trocarFonte}
                    />
                    , mostrar{' '}
                    {consulta.medidas.map((m, i) => (
                        <span key={m.id} className="rl-frase-item">
                            <Lacuna
                                valor={nomeMedida(m.id)}
                                opcoes={(fonte?.medidas ?? []).map(x => ({
                                    id: x.id, nome: x.nome, nota: x.definicao,
                                    desabilitada: x.id !== m.id && consulta.medidas.some(y => y.id === x.id),
                                }))}
                                onEscolher={id => mudarConsulta({
                                    medidas: consulta.medidas.map((y, j) => (j === i ? { id } : y)),
                                })}
                            />
                            {consulta.medidas.length > 1 && (
                                <button type="button" className="rl-tira" aria-label={`Tirar ${nomeMedida(m.id)}`}
                                    onClick={() => mudarConsulta({ medidas: consulta.medidas.filter((_, j) => j !== i) })}>
                                    <LuX size={10} />
                                </button>
                            )}
                            {i < consulta.medidas.length - 1 && <span className="rl-frase-e">e</span>}
                        </span>
                    ))}
                    {medidasLivres.length > 0 && consulta.medidas.length < catalogo.limites.maximoMedidas && (
                        <Lacuna
                            valor=""
                            vazio="mais um número"
                            opcoes={medidasLivres.map(m => ({ id: m.id, nome: m.nome, nota: m.definicao }))}
                            onEscolher={id => mudarConsulta({ medidas: [...consulta.medidas, { id }] })}
                        />
                    )}

                    {consulta.dimensoes.length > 0 && (
                        <>
                            <br />separado por{' '}
                            {consulta.dimensoes.map((d, i) => (
                                <span key={d} className="rl-frase-item">
                                    <Lacuna
                                        valor={nomeDimensao(d)}
                                        opcoes={(fonte?.dimensoes ?? []).map(x => ({
                                            id: x.id, nome: x.nome,
                                            desabilitada: x.id !== d && consulta.dimensoes.includes(x.id),
                                        }))}
                                        onEscolher={id => mudarConsulta({
                                            dimensoes: consulta.dimensoes.map((y, j) => (j === i ? id : y)),
                                        })}
                                    />
                                    <button type="button" className="rl-tira" aria-label={`Tirar ${nomeDimensao(d)}`}
                                        onClick={() => mudarConsulta({ dimensoes: consulta.dimensoes.filter((_, j) => j !== i) })}>
                                        <LuX size={10} />
                                    </button>
                                    {i < consulta.dimensoes.length - 1 && <span className="rl-frase-e">e depois</span>}
                                </span>
                            ))}
                        </>
                    )}

                    {dimensoesLivres.length > 0 && consulta.dimensoes.length < catalogo.limites.maximoDimensoes && (
                        <>
                            <br />
                            <Lacuna
                                valor=""
                                vazio={consulta.dimensoes.length === 0 ? 'separar por…' : 'e depois por…'}
                                opcoes={dimensoesLivres.map(d => ({ id: d.id, nome: d.nome }))}
                                onEscolher={id => mudarConsulta({ dimensoes: [...consulta.dimensoes, id] })}
                            />
                        </>
                    )}

                    {(fonte?.datas.length ?? 0) > 1 && (
                        <>
                            <br />contando pela data de{' '}
                            <Lacuna
                                valor={fonte?.datas.find(d => d.id === consulta.periodo.data)?.nome ?? ''}
                                opcoes={(fonte?.datas ?? []).map(d => ({ id: d.id, nome: d.nome }))}
                                onEscolher={id => mudarConsulta({ periodo: { ...consulta.periodo, data: id } })}
                            />
                        </>
                    )}
                </p>

                {/* ── filtros ── */}
                {consulta.filtros.map((f, i) => {
                    const lista = valores[f.dimensao];
                    const rotulos = f.valores
                        .map(v => lista?.find(x => x.id === v)?.nome ?? v)
                        .join(', ');
                    return (
                        <p key={`${f.dimensao}-${i}`} className="rl-frase is-filtro">
                            só onde <b>{nomeDimensao(f.dimensao)}</b> é{' '}
                            <Lacuna
                                valor={rotulos}
                                vazio="qualquer valor"
                                multipla
                                escolhidos={f.valores}
                                opcoes={(lista ?? []).map(v => ({ id: v.id, nome: v.nome }))}
                                onEscolher={id => {
                                    const marcados = f.valores.includes(id)
                                        ? f.valores.filter(v => v !== id)
                                        : [...f.valores, id];
                                    mudarConsulta({
                                        filtros: consulta.filtros.map((y, j) => (j === i ? { ...y, valores: marcados } : y)),
                                    });
                                }}
                            />
                            <button type="button" className="rl-tira" aria-label="Tirar filtro"
                                onClick={() => mudarConsulta({ filtros: consulta.filtros.filter((_, j) => j !== i) })}>
                                <LuX size={10} />
                            </button>
                        </p>
                    );
                })}

                {consulta.filtros.length < catalogo.limites.maximoFiltros && (fonte?.dimensoes.length ?? 0) > 0 && (
                    <div className="rl-frase-acoes">
                        <Lacuna
                            valor=""
                            vazio="filtrar"
                            opcoes={(fonte?.dimensoes ?? [])
                                .filter(d => !consulta.filtros.some(f => f.dimensao === d.id))
                                .map(d => ({ id: d.id, nome: d.nome }))}
                            onEscolher={id => mudarConsulta({
                                filtros: [...consulta.filtros, { dimensao: id, operador: 'in', valores: [] }],
                            })}
                        />
                        <LuFilter size={11} className="rl-frase-ic" />
                    </div>
                )}

                {/* ── formato ── */}
                <p className="rl-lat-l">Como mostrar</p>
                <div className="rl-lat-vis">
                    {formatos.map(f => (
                        <button
                            key={f.id}
                            type="button"
                            className={`rl-lat-v ${consulta.visual === f.id ? 'is-on' : ''} ${f.impedimento ? 'is-off' : ''}`}
                            disabled={!!f.impedimento}
                            title={f.impedimento ?? f.rotulo}
                            onClick={() => trocarVisual(f.id)}
                        >
                            {f.icone}<span>{f.rotulo}</span>
                        </button>
                    ))}
                </div>
                {formatos.find(f => f.id === consulta.visual)?.impedimento && (
                    <p className="rl-lat-n">{formatos.find(f => f.id === consulta.visual)?.impedimento}</p>
                )}

                {/* ── título ── */}
                <p className="rl-lat-l">Título do bloco</p>
                <input
                    className="rl-lat-in"
                    value={bloco.titulo ?? ''}
                    placeholder="Ex.: Vendas por pessoa"
                    onChange={e => onMudar({ ...bloco, titulo: e.target.value })}
                />

                <p className="rl-lat-n is-fim">
                    O período vem do painel: <b>{periodoEmPalavras}</b>. Trocar no topo muda todos os blocos.
                </p>
            </div>
        </aside>
    );
}

/** Um bloco novo, já válido: a primeira medida da primeira fonte, em tabela. */
export function blocoNovo(catalogo: Catalogo, id: string, x: number, y: number): BlocoPainel {
    const fonte = catalogo.fontes[0];
    return {
        id,
        titulo: 'Novo bloco',
        x, y, w: 6, h: 3,
        consulta: {
            fonte: fonte.id,
            nivel: 'agregado',
            medidas: fonte.medidas[0] ? [{ id: fonte.medidas[0].id }] : [],
            dimensoes: [],
            filtros: [],
            periodo: { data: fonte.datas[0]?.id ?? 'criado_em', preset: 'ultimos_30_dias' },
            visual: 'numero',
        },
    };
}
